import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLinkIcon, TrendingUpIcon, TrendingDownIcon, CheckIcon, XIcon, ArrowLeftIcon } from 'lucide-react';
import { Card } from '../components/Card';
import { TrustScore } from '../components/TrustScore';
import { ReviewCard } from '../components/ReviewCard';
import { Button } from '../components/Button';
import { cryptocurrencies } from '../data/mockData';
export function CryptoDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const crypto = cryptocurrencies.find(c => c.id === id);
  if (!crypto) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-161">
        <div className="text-center" data-id="element-162">
          <h1 className="text-2xl font-bold mb-4" data-id="element-163">Cryptocurrency not found</h1>
          <Link to="/cryptocurrencies" data-id="element-164">
            <Button data-id="element-165">Back to Cryptocurrencies</Button>
          </Link>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50" data-id="element-166">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-12" data-id="element-167">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-168">
          <Link to="/cryptocurrencies" className="inline-flex items-center gap-2 text-slate-300 hover:text-white mb-6 transition-colors" data-id="element-169">
            <ArrowLeftIcon size={20} data-id="element-170" />
            <span data-id="element-171">Back to Cryptocurrencies</span>
          </Link>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-172">
            <div className="flex items-center gap-4" data-id="element-173">
              <img src={crypto.logo} alt={crypto.name} className="w-16 h-16 rounded-full bg-white/10 p-2" data-id="element-174" />
              <div data-id="element-175">
                <h1 className="text-4xl font-bold mb-2" data-id="element-176">{crypto.name}</h1>
                <div className="flex items-center gap-3" data-id="element-177">
                  <span className="text-slate-300 text-lg" data-id="element-178">
                    {crypto.symbol}
                  </span>
                  <TrustScore score={crypto.trustScore} size="md" data-id="element-179" />
                </div>
              </div>
            </div>

            <div className="text-right" data-id="element-180">
              <div className="text-3xl font-bold mb-2" data-id="element-181">
                ${crypto.price.toLocaleString()}
              </div>
              <div className={`flex items-center justify-end gap-1 text-lg font-semibold ${crypto.change24h >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-182">
                {crypto.change24h >= 0 ? <TrendingUpIcon size={20} data-id="element-183" /> : <TrendingDownIcon size={20} data-id="element-184" />}
                <span data-id="element-185">
                  {crypto.change24h >= 0 ? '+' : ''}
                  {crypto.change24h.toFixed(2)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-186">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-187">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8" data-id="element-188">
            {/* Key Metrics */}
            <Card className="p-6" data-id="element-189">
              <h2 className="text-2xl font-bold mb-6" data-id="element-190">Key Metrics</h2>
              <div className="grid grid-cols-2 gap-6" data-id="element-191">
                <div data-id="element-192">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-193">Market Cap</div>
                  <div className="text-xl font-semibold" data-id="element-194">
                    ${(crypto.marketCap / 1000000000).toFixed(2)}B
                  </div>
                </div>
                <div data-id="element-195">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-196">24h Volume</div>
                  <div className="text-xl font-semibold" data-id="element-197">
                    ${(crypto.volume24h / 1000000000).toFixed(2)}B
                  </div>
                </div>
                <div data-id="element-198">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-199">Rank</div>
                  <div className="text-xl font-semibold" data-id="element-200">#{crypto.rank}</div>
                </div>
                <div data-id="element-201">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-202">Trust Score</div>
                  <div className="text-xl font-semibold" data-id="element-203">
                    {crypto.trustScore.toFixed(1)}/10
                  </div>
                </div>
              </div>
            </Card>

            {/* About */}
            <Card className="p-6" data-id="element-204">
              <h2 className="text-2xl font-bold mb-4" data-id="element-205">About {crypto.name}</h2>
              <p className="text-slate-700 leading-relaxed mb-6" data-id="element-206">
                {crypto.description}
              </p>

              <div className="flex flex-wrap gap-3" data-id="element-207">
                <a href={crypto.website} target="_blank" rel="noopener noreferrer" data-id="element-208">
                  <Button variant="outline" className="flex items-center gap-2" data-id="element-209">
                    <span data-id="element-210">Official Website</span>
                    <ExternalLinkIcon size={16} data-id="element-211" />
                  </Button>
                </a>
                {crypto.whitepaper && <a href={crypto.whitepaper} target="_blank" rel="noopener noreferrer" data-id="element-212">
                    <Button variant="outline" className="flex items-center gap-2" data-id="element-213">
                      <span data-id="element-214">Whitepaper</span>
                      <ExternalLinkIcon size={16} data-id="element-215" />
                    </Button>
                  </a>}
              </div>
            </Card>

            {/* Pros & Cons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-216">
              <Card className="p-6" data-id="element-217">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-218">
                  <CheckIcon className="text-green-600" size={24} data-id="element-219" />
                  <span data-id="element-220">Pros</span>
                </h3>
                <ul className="space-y-2" data-id="element-221">
                  {crypto.pros.map((pro, index) => <li key={index} className="flex items-start gap-2" data-id="element-222">
                      <CheckIcon className="text-green-600 flex-shrink-0 mt-0.5" size={16} data-id="element-223" />
                      <span className="text-slate-700" data-id="element-224">{pro}</span>
                    </li>)}
                </ul>
              </Card>

              <Card className="p-6" data-id="element-225">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-226">
                  <XIcon className="text-red-600" size={24} data-id="element-227" />
                  <span data-id="element-228">Cons</span>
                </h3>
                <ul className="space-y-2" data-id="element-229">
                  {crypto.cons.map((con, index) => <li key={index} className="flex items-start gap-2" data-id="element-230">
                      <XIcon className="text-red-600 flex-shrink-0 mt-0.5" size={16} data-id="element-231" />
                      <span className="text-slate-700" data-id="element-232">{con}</span>
                    </li>)}
                </ul>
              </Card>
            </div>

            {/* Reviews */}
            <div data-id="element-233">
              <h2 className="text-2xl font-bold mb-6" data-id="element-234">User Reviews</h2>
              <div className="space-y-4" data-id="element-235">
                {crypto.reviews.map(review => <ReviewCard key={review.id} review={review} data-id="element-236" />)}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-237">
            <Card className="p-6" data-id="element-238">
              <h3 className="font-bold text-lg mb-4" data-id="element-239">Quick Stats</h3>
              <div className="space-y-4" data-id="element-240">
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-241">
                  <span className="text-slate-600" data-id="element-242">Current Price</span>
                  <span className="font-semibold" data-id="element-243">
                    ${crypto.price.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-244">
                  <span className="text-slate-600" data-id="element-245">Market Cap Rank</span>
                  <span className="font-semibold" data-id="element-246">#{crypto.rank}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-247">
                  <span className="text-slate-600" data-id="element-248">24h Change</span>
                  <span className={`font-semibold ${crypto.change24h >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-249">
                    {crypto.change24h >= 0 ? '+' : ''}
                    {crypto.change24h.toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-250">
                  <span className="text-slate-600" data-id="element-251">Total Reviews</span>
                  <span className="font-semibold" data-id="element-252">{crypto.reviews.length}</span>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200" data-id="element-253">
              <h3 className="font-bold text-lg mb-2" data-id="element-254">Trust Score Breakdown</h3>
              <div className="text-3xl font-bold text-blue-600 mb-4" data-id="element-255">
                {crypto.trustScore.toFixed(1)}/10
              </div>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-256">
                Our trust score is calculated based on security audits, team
                transparency, community sentiment, and historical performance.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}